import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Filter, Download, AlertCircle, CheckCircle, Clock, XCircle, BarChart3, Wifi, WifiOff, Settings, AlertTriangle } from 'lucide-react';
import { n8nWebhookLogsService } from '../../services/n8nWebhookLogsService';
import AdminNavigation from '../../components/ui/AdminNavigation';
import MobileAdminHeader from '../../components/ui/MobileAdminHeader';
import RetrySuccessChart from './components/RetrySuccessChart';
import FailureTrendsChart from './components/FailureTrendsChart';
import PeakFailureTimesChart from './components/PeakFailureTimesChart';
import ContainerAnalysisTable from './components/ContainerAnalysisTable';
import RetryPolicyConfig from './components/RetryPolicyConfig';
import DeadLetterQueuePanel from './components/DeadLetterQueuePanel';

export default function N8nWebhookMonitoringDashboard() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [batchRetrying, setBatchRetrying] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [showRetryConfig, setShowRetryConfig] = useState(false);
  const [showDLQ, setShowDLQ] = useState(false);
  const [autoRetrying, setAutoRetrying] = useState(false);
  const [autoRetryResults, setAutoRetryResults] = useState(null);
  
  // N8N Configuration states - modified to track original values for comparison
  const [n8nConfig, setN8nConfig] = useState({
    webhookUrl: '',
    apiKey: '',
    enabled: false
  });
  const [originalN8nConfig, setOriginalN8nConfig] = useState({
    webhookUrl: '',
    apiKey: '',
    enabled: false
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    containerReference: '',
    dateFrom: '',
    dateTo: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter logs by search term (moved before usage)
  const filteredLogs = logs?.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm?.toLowerCase();
    return (
      log?.id?.toLowerCase()?.includes(search) ||
      log?.container?.containerCode?.toLowerCase()?.includes(search) ||
      log?.errorMessage?.toLowerCase()?.includes(search)
    );
  });

  const failedLogsCount = filteredLogs?.filter(log => 
    log?.status === 'failed' || log?.status === 'retrying'
  )?.length;

  // Load data on mount and setup realtime subscriptions
  useEffect(() => {
    loadData();
    loadAnalytics();
    loadN8nConfig(); // Load n8n configuration from database
    setupRealtimeSubscription();

    // Cleanup subscription on unmount
    return () => {
      if (realtimeChannel) {
        n8nWebhookLogsService?.unsubscribeFromWebhookUpdates(realtimeChannel);
      }
    };
  }, []);

  // Apply filters when they change
  useEffect(() => {
    if (!loading) {
      applyFilters();
    }
  }, [filters]);

  const loadN8nConfig = async () => {
    try {
      setLoadingConfig(true);
      const config = await n8nWebhookLogsService?.getN8nProviderConfig();
      setN8nConfig(config);
      setOriginalN8nConfig(config); // Store original values for comparison
    } catch (err) {
      console.error('Error loading n8n config:', err);
      setError('Erreur lors du chargement de la configuration n8n');
    } finally {
      setLoadingConfig(false);
    }
  };

  const setupRealtimeSubscription = () => {
    try {
      console.log(`🚀 Attempting to setup realtime subscription (attempt ${retryAttempt + 1})...`);
      
      const channel = n8nWebhookLogsService?.subscribeToWebhookUpdates(
        // onInsert callback
        async (newLog) => {
          console.log('🔔 New webhook log received:', newLog);
          
          // Fetch full log details including container info
          try {
            const fullLog = await n8nWebhookLogsService?.getById(newLog?.id);
            setLogs(prev => [fullLog, ...prev]);
            
            // Update statistics
            await loadStatistics();
            
            // Show notification toast
            showNotification('Nouveau webhook ajouté', 'success');
          } catch (err) {
            console.error('Error fetching full log details:', err);
          }
        },
        // onUpdate callback
        async (updatedLog) => {
          console.log('🔄 Webhook log updated:', updatedLog);
          
          // Fetch full log details including container info
          try {
            const fullLog = await n8nWebhookLogsService?.getById(updatedLog?.id);
            setLogs(prev => prev?.map(log => 
              log?.id === fullLog?.id ? fullLog : log
            ));
            
            // Update statistics
            await loadStatistics();
            
            // Show notification based on status change
            if (updatedLog?.status === 'success') {
              showNotification('Webhook delivery réussi', 'success');
            } else if (updatedLog?.status === 'failed') {
              showNotification('Webhook delivery échoué', 'error');
            } else if (updatedLog?.status === 'retrying') {
              showNotification('Webhook en cours de retry', 'info');
            }
          } catch (err) {
            console.error('Error fetching updated log details:', err);
          }
        },
        // onDelete callback (optional)
        null,
        // onConnectionChange callback
        (connected, error) => {
          console.log('📡 Connection status changed:', { connected, error });
          setRealtimeConnected(connected);
          
          if (connected) {
            setConnectionError(null);
            setRetryAttempt(0);
            showNotification('Connexion temps réel établie', 'success');
          } else {
            setConnectionError(error);
            
            // Retry connection after delay if it failed
            if (retryAttempt < 3) {
              const retryDelay = Math.min(1000 * Math.pow(2, retryAttempt), 10000); // Exponential backoff, max 10s
              console.log(`⏳ Retrying connection in ${retryDelay}ms...`);
              
              setTimeout(() => {
                setRetryAttempt(prev => prev + 1);
                setupRealtimeSubscription();
              }, retryDelay);
            } else {
              console.error('❌ Max retry attempts reached. Realtime updates disabled.');
              showNotification('Connexion temps réel échouée', 'error');
            }
          }
        }
      );

      setRealtimeChannel(channel);
    } catch (err) {
      console.error('💥 Failed to setup realtime subscription:', err);
      setRealtimeConnected(false);
      setConnectionError(err?.message);
      showNotification('Erreur de connexion temps réel', 'error');
    }
  };

  const loadStatistics = async () => {
    try {
      const statsData = await n8nWebhookLogsService?.getStatistics();
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const showNotification = (message, type) => {
    // Create a temporary notification element
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
      type === 'error'? 'bg-red-100 text-red-800 border border-red-200' : 'bg-blue-100 text-blue-800 border border-blue-200'
    }`;
    notification.textContent = message;
    document.body?.appendChild(notification);

    // Remove notification after 3 seconds
    setTimeout(() => {
      notification?.remove();
    }, 3000);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [logsData, statsData] = await Promise.all([
        n8nWebhookLogsService?.getAll(),
        n8nWebhookLogsService?.getStatistics()
      ]);
      setLogs(logsData);
      setStatistics(statsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const analyticsData = await n8nWebhookLogsService?.getAnalyticsDashboard();
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const applyFilters = async () => {
    try {
      setLoading(true);
      const filteredData = await n8nWebhookLogsService?.getAll(filters);
      setLogs(filteredData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (logId) => {
    try {
      await n8nWebhookLogsService?.retry(logId);
      await loadData(); // Reload to show updated status
    } catch (err) {
      setError(`Retry failed: ${err?.message}`);
    }
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => {
      if (prev?.includes(logId)) {
        return prev?.filter(id => id !== logId);
      } else {
        return [...prev, logId];
      }
    });
  };

  const handleSelectAll = () => {
    const failedLogIds = filteredLogs
      ?.filter(log => log?.status === 'failed' || log?.status === 'retrying')
      ?.map(log => log?.id);
    
    if (selectedLogs?.length === failedLogIds?.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(failedLogIds);
    }
  };

  const handleBatchRetry = async () => {
    if (selectedLogs?.length === 0) {
      setError('Veuillez sélectionner au moins un webhook à relancer');
      return;
    }

    try {
      setBatchRetrying(true);
      setBatchResults(null);
      setError('');

      const results = await n8nWebhookLogsService?.batchRetry(selectedLogs);
      setBatchResults(results);
      
      // Clear selection and reload data
      setSelectedLogs([]);
      await loadData();
      await loadAnalytics(); // Refresh analytics after batch retry
    } catch (err) {
      setError(`Batch retry failed: ${err?.message}`);
    } finally {
      setBatchRetrying(false);
    }
  };

  const handleAutoRetry = async () => {
    try {
      setAutoRetrying(true);
      setError('');
      setAutoRetryResults(null);

      const results = await n8nWebhookLogsService?.autoRetryFailedWebhooks();
      setAutoRetryResults(results);
      
      // Reload data after auto-retry
      await loadData();
      await loadAnalytics();

      showNotification(`Auto-retry terminé: ${results?.success} succès, ${results?.failed} échecs`, 'success');
    } catch (err) {
      setError(`Auto-retry échoué: ${err?.message}`);
      showNotification('Erreur auto-retry', 'error');
    } finally {
      setAutoRetrying(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      containerReference: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
  };

  const exportLogs = () => {
    const csv = [
      ['ID', 'Container', 'Status', 'HTTP Code', 'Retry Count', 'Created At', 'Error Message']?.join(','),
      ...logs?.map(log => [
        log?.id,
        log?.container?.containerCode || 'N/A',
        log?.status,
        log?.httpStatusCode || 'N/A',
        log?.retryCount,
        new Date(log?.createdAt)?.toLocaleString(),
        log?.errorMessage || ''
      ]?.join(','))
    ]?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `n8n-webhook-logs-${new Date()?.toISOString()}.csv`;
    a?.click();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'retrying':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      retrying: 'bg-blue-100 text-blue-800'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleSaveN8nConfig = async () => {
    try {
      setSavingConfig(true);
      setError('');
      
      // Validate webhook URL
      if (!n8nConfig?.webhookUrl?.trim()) {
        setError('L\'URL du webhook est obligatoire');
        return;
      }
      
      // Validate URL format
      try {
        new URL(n8nConfig?.webhookUrl);
      } catch (err) {
        setError('Format d\'URL invalide');
        return;
      }

      // Save configuration to database
      const savedConfig = await n8nWebhookLogsService?.saveN8nProviderConfig(n8nConfig);
      
      // Update original config to reflect saved values
      setOriginalN8nConfig(savedConfig);
      
      showNotification('Configuration n8n enregistrée avec succès', 'success');
    } catch (err) {
      setError(`Erreur lors de l'enregistrement: ${err?.message}`);
      showNotification('Erreur enregistrement config', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCancelN8nConfig = () => {
    // Reset to original values from database
    setN8nConfig(originalN8nConfig);
    setError('');
  };

  // Check if configuration has changed from original
  const hasConfigChanged = () => {
    return (
      n8nConfig?.webhookUrl !== originalN8nConfig?.webhookUrl ||
      n8nConfig?.apiKey !== originalN8nConfig?.apiKey ||
      n8nConfig?.enabled !== originalN8nConfig?.enabled
    );
  };

  if (loading && !logs?.length) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MobileAdminHeader />
        <AdminNavigation />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-green-600 mb-2" />
            <p className="text-gray-600">Chargement des logs webhooks...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MobileAdminHeader />
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Monitoring Webhooks n8n
              </h1>
              <p className="text-gray-600">
                Surveillance des notifications conteneur via webhooks n8n avec statuts de livraison et logs de retry
              </p>
            </div>
            {/* Real-time Status Indicator */}
            <div className="flex items-center gap-2">
              {realtimeConnected ? (
                <>
                  <Wifi className="w-5 h-5 text-green-600 animate-pulse" />
                  <span className="text-sm font-medium text-green-600">Temps réel actif</span>
                </>
              ) : connectionError ? (
                <>
                  <WifiOff className="w-5 h-5 text-red-400" />
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-medium text-red-600">Connexion échouée</span>
                    {retryAttempt > 0 && retryAttempt < 3 && (
                      <span className="text-xs text-gray-500">Nouvelle tentative {retryAttempt}/3...</span>
                    )}
                    {retryAttempt >= 3 && (
                      <button 
                        onClick={() => {
                          setRetryAttempt(0);
                          setupRealtimeSubscription();
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Réessayer
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-400">Connexion en cours...</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* N8N Configuration Section - NOW AT TOP */}
        <div className="bg-white rounded-lg shadow mb-8 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-teal-50 to-teal-100 px-6 py-4 border-b border-teal-200 cursor-pointer hover:from-teal-100 hover:to-teal-200 transition-colors"
            onClick={() => setConfigExpanded(!configExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-teal-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Configuration n8n Provider</h2>
                  <p className="text-sm text-gray-600">Paramètres de connexion webhook pour notifications automatiques</p>
                </div>
              </div>
              <button className="text-teal-600 hover:text-teal-800">
                {configExpanded ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {configExpanded && (
            <div className="p-6">
              {loadingConfig ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-teal-600 mr-2" />
                  <span className="text-gray-600">Chargement de la configuration...</span>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                      {/* Webhook URL Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          URL du Webhook <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          value={n8nConfig?.webhookUrl}
                          onChange={(e) => setN8nConfig(prev => ({ ...prev, webhookUrl: e?.target?.value }))}
                          placeholder="https://n8n.example.com/webhook/whatsapp"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                          required
                        />
                        <p className="mt-2 text-sm text-gray-500 flex items-start gap-1">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          URL complète du webhook n8n pour recevoir les notifications de statut
                        </p>
                      </div>

                      {/* API Key Field (Optional) */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          API Key (optionnel)
                        </label>
                        <input
                          type="password"
                          value={n8nConfig?.apiKey}
                          onChange={(e) => setN8nConfig(prev => ({ ...prev, apiKey: e?.target?.value }))}
                          placeholder="Clé d'authentification sécurisée"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                        />
                        <p className="mt-2 text-sm text-gray-500 flex items-start gap-1">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          Clé API pour authentification des appels webhook (si requise par n8n)
                        </p>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      {/* Enable Provider Checkbox */}
                      <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                        <div className="flex items-start gap-4">
                          <input
                            type="checkbox"
                            id="n8n-enabled-inline"
                            checked={n8nConfig?.enabled}
                            onChange={(e) => setN8nConfig(prev => ({ ...prev, enabled: e?.target?.checked }))}
                            className="w-6 h-6 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-1"
                          />
                          <div className="flex-1">
                            <label htmlFor="n8n-enabled-inline" className="text-base font-semibold text-gray-900 cursor-pointer">
                              Activer ce provider
                            </label>
                            <p className="mt-2 text-sm text-gray-600">
                              Activez pour commencer à envoyer les notifications de changement de statut vers n8n
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-900">
                            <p className="font-semibold mb-2">À propos de l'intégration n8n</p>
                            <ul className="space-y-1 list-disc list-inside">
                              <li>Envoi automatique lors des changements de statut</li>
                              <li>Notifications pour conteneurs et livraisons</li>
                              <li>Authentification optionnelle via API Key</li>
                              <li>Monitoring des tentatives et logs détaillés</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Display saved status if not changed */}
                      {!hasConfigChanged() && originalN8nConfig?.webhookUrl && (
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-green-900">
                              <p className="font-semibold">Configuration enregistrée</p>
                              <p className="text-xs text-green-700 mt-1">
                                Dernière mise à jour: {originalN8nConfig?.updatedAt ? new Date(originalN8nConfig?.updatedAt)?.toLocaleString() : 'Inconnue'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleCancelN8nConfig}
                      disabled={!hasConfigChanged()}
                      className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveN8nConfig}
                      disabled={savingConfig || !n8nConfig?.webhookUrl || !hasConfigChanged()}
                      className="flex items-center px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg"
                    >
                      {savingConfig ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Enregistrer
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Erreur</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-600 hover:text-red-800"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Batch Results Display */}
        {batchResults && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-blue-800 font-medium">Résultats du batch retry</p>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Total: {batchResults?.total} webhooks</p>
                  <p className="text-green-700">✓ Succès: {batchResults?.success}</p>
                  <p className="text-red-700">✗ Échecs: {batchResults?.failed}</p>
                </div>
              </div>
              <button
                onClick={() => setBatchResults(null)}
                className="text-blue-600 hover:text-blue-800"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Auto-Retry Results Display */}
        {autoRetryResults && (
          <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start">
              <RefreshCw className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-purple-800 font-medium">Résultats Auto-Retry</p>
                <div className="mt-2 text-sm text-purple-700">
                  <p>Total traité: {autoRetryResults?.total} webhooks</p>
                  <p className="text-green-700">✓ Succès: {autoRetryResults?.success}</p>
                  <p className="text-red-700">✗ Échecs: {autoRetryResults?.failed}</p>
                  <p className="text-orange-700">📥 Déplacés vers DLQ: {autoRetryResults?.movedToDLQ}</p>
                </div>
              </div>
              <button
                onClick={() => setAutoRetryResults(null)}
                className="text-purple-600 hover:text-purple-800"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons Row */}
        <div className="mb-6 flex flex-wrap gap-3 justify-end">
          <button
            onClick={() => setShowRetryConfig(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Settings className="w-5 h-5 mr-2" />
            Config Retry Policy
          </button>

          <button
            onClick={() => setShowDLQ(!showDLQ)}
            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <AlertTriangle className="w-5 h-5 mr-2" />
            {showDLQ ? 'Masquer DLQ' : 'Afficher DLQ'}
          </button>

          <button
            onClick={handleAutoRetry}
            disabled={autoRetrying}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${autoRetrying ? 'animate-spin' : ''}`} />
            {autoRetrying ? 'Auto-Retry...' : 'Lancer Auto-Retry'}
          </button>

          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            {showAnalytics ? 'Masquer Analytics' : 'Afficher Analytics'}
          </button>
        </div>

        {/* Retry Policy Config Modal */}
        {showRetryConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <RetryPolicyConfig onClose={() => setShowRetryConfig(false)} />
          </div>
        )}

        {/* Dead Letter Queue Panel */}
        {showDLQ && (
          <div className="mb-8">
            <DeadLetterQueuePanel />
          </div>
        )}

        {/* Analytics Toggle Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            {showAnalytics ? 'Masquer Analytics' : 'Afficher Analytics'}
          </button>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && analytics && (
          <div className="mb-8 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RetrySuccessChart data={analytics?.retryAnalytics} />
              <FailureTrendsChart data={analytics?.failureTrends} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PeakFailureTimesChart data={analytics?.peakTimes} />
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Métriques Performance</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Taux Succès Global Relances</p>
                    <p className="text-3xl font-bold text-green-600">{analytics?.retryAnalytics?.successRate}%</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Total Relances Effectuées</p>
                    <p className="text-3xl font-bold text-blue-600">{analytics?.retryAnalytics?.totalRetries}</p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Heures Pic d'Échec</p>
                    <p className="text-lg font-bold text-orange-600">{analytics?.peakTimes?.peakHours?.join(', ')}</p>
                  </div>
                </div>
              </div>
            </div>
            <ContainerAnalysisTable data={analytics?.containerAnalysis} />
          </div>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.total}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Succès</p>
                  <p className="text-2xl font-bold text-green-600">{statistics?.success}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Échecs</p>
                  <p className="text-2xl font-bold text-red-600">{statistics?.failed}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{statistics?.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Taux de succès</p>
                  <p className="text-2xl font-bold text-blue-600">{statistics?.successRate}%</p>
                </div>
                <RefreshCw className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  placeholder="Rechercher par ID, référence conteneur, erreur..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filtres
              </button>

              <button
                onClick={loadData}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>

              <button
                onClick={exportLogs}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="w-5 h-5 mr-2" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={filters?.status}
                    onChange={(e) => handleFilterChange('status', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Tous</option>
                    <option value="success">Succès</option>
                    <option value="failed">Échec</option>
                    <option value="pending">En attente</option>
                    <option value="retrying">Retry en cours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Référence conteneur
                  </label>
                  <input
                    type="text"
                    value={filters?.containerReference}
                    onChange={(e) => handleFilterChange('containerReference', e?.target?.value)}
                    placeholder="CNT-XXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date début
                  </label>
                  <input
                    type="date"
                    value={filters?.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date fin
                  </label>
                  <input
                    type="date"
                    value={filters?.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  Effacer les filtres
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Batch Actions Bar */}
        {failedLogsCount > 0 && (
          <div className="bg-white rounded-lg shadow mb-6 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {selectedLogs?.length} webhook(s) sélectionné(s) sur {failedLogsCount} échec(s)
                </span>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {selectedLogs?.length === failedLogsCount ? 'Désélectionner tout' : 'Sélectionner tout'}
                </button>
              </div>
              <button
                onClick={handleBatchRetry}
                disabled={selectedLogs?.length === 0 || batchRetrying}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${batchRetrying ? 'animate-spin' : ''}`} />
                {batchRetrying ? 'Relance en cours...' : 'Retry Selected'}
              </button>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedLogs?.length > 0 && selectedLogs?.length === failedLogsCount}
                      onChange={handleSelectAll}
                      disabled={failedLogsCount === 0}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conteneur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HTTP Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Retry Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Erreur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      Aucun log webhook trouvé
                    </td>
                  </tr>
                ) : (
                  filteredLogs?.map((log) => (
                    <tr key={log?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(log?.status === 'failed' || log?.status === 'retrying') ? (
                          <input
                            type="checkbox"
                            checked={selectedLogs?.includes(log?.id)}
                            onChange={() => handleSelectLog(log?.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        ) : (
                          <div className="w-4 h-4" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(log?.status)}
                          <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(log?.status)}`}>
                            {log?.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {log?.container?.containerCode || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {log?.container?.status || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${
                          log?.httpStatusCode >= 200 && log?.httpStatusCode < 300
                            ? 'text-green-600'
                            : log?.httpStatusCode >= 400
                            ? 'text-red-600' :'text-gray-600'
                        }`}>
                          {log?.httpStatusCode || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log?.retryCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(log.createdAt)?.toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(log.createdAt)?.toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {log?.errorMessage || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {(log?.status === 'failed' || log?.status === 'retrying') && (
                          <button
                            onClick={() => handleRetry(log?.id)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Retry
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-center text-sm text-gray-600">
          Affichage de {filteredLogs?.length} log(s) sur {logs?.length} total
        </div>
      </div>
    </div>
  );
}